import { ContactType, GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { getEnumKeyByValue, getEnumValueByKey } from '@/shared/utils/enums';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloError, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { ContactType as Trace8ContactType, generateSchemaFromRows } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { RowComponent } from '../templateComponents';
import { IRenderTemplatePage, IRenderTemplateRow } from '../templateComponents/types';
import { CREATE_CONTACT_MUTATION, UPDATE_CONTACT_MUTATION } from './queries';

interface EnterContactInfoModalPageProps {
  row: IRenderTemplateRow;
  pageName?: string;
  currentItemId: string | null;
  tenantId: string | null;
  entityId: string;
  contacts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'];
  currentEditPage: IRenderTemplatePage;
  ignoreRequired?: boolean;
  getContactIds: (name: string) => string | string[];
  saveContactIds: (name: string, ids: string | string[]) => void;
  bankAccountsAndContactsRefetch: () => void;
  closeModalPage: () => void;
}

export const EnterContactInfoModalPage = ({
  row,
  pageName = '',
  currentItemId,
  tenantId,
  entityId,
  contacts,
  currentEditPage,
  ignoreRequired = false,
  getContactIds,
  saveContactIds,
  bankAccountsAndContactsRefetch,
  closeModalPage
}: EnterContactInfoModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [isModalWindowOpen, setIsModalWindowOpen] = useState(true);
  const [selectedContactType, setSelectedContactType] = useState<ContactType>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createContact] = useMutation(CREATE_CONTACT_MUTATION);
  const [updateContact] = useMutation(UPDATE_CONTACT_MUTATION);

  const rowsToRender = row?.config?.addContactRows
    ? Array.isArray(row?.config?.addContactRows)
      ? row?.config?.addContactRows
      : row?.config?.addContactRows[selectedContactType]
    : [];
  const schema = rowsToRender && rowsToRender.length > 0 ? generateSchemaFromRows(rowsToRender, ignoreRequired) : null;
  const methods = useForm<Record<string, any>>({
    mode: 'onChange',
    resolver: schema ? zodResolver(schema) : null
  });

  useEffect(() => {
    if (!currentItemId) return;
    try {
      const currentContact = contacts.find((contact) => contact.id === currentItemId);
      const contactData = currentContact?.data;
      methods.reset((defaultValues) => ({
        ...defaultValues,
        ...contactData,
        name: currentContact.name,
        email: currentContact.email
      }));
      setSelectedContactType(getEnumValueByKey(Trace8ContactType, currentContact.type) as ContactType);
      setIsEditMode(true);
    } catch (error) {
      console.error(error);
    }
  }, [currentItemId]);

  const onSave = async (data: any) => {
    try {
      setErrorMessage(null);
      setIsSubmitting(true);
      const { name, email, ...rest } = data;

      if (isEditMode) {
        const { data: updateContactData } = await updateContact({
          variables: {
            input: {
              id: currentItemId,
              name,
              email,
              data: rest,
              tenantId
            }
          }
        });

        const { id } = updateContactData.updateContact;
        const contactIds = getContactIds(`${pageName}.${row.name}`);
        if (row?.config?.selectType === 'multiple') {
          saveContactIds(`${pageName}.${row.name}`, (contactIds || []).includes(id) ? contactIds : [...(contactIds || []), id]);
        } else {
          saveContactIds(`${pageName}.${row.name}`, id);
        }
        bankAccountsAndContactsRefetch();
        closeModalPage();
        return;
      }

      if (!isEditMode) {
        const { data: createContactData } = await createContact({
          variables: {
            input: {
              legalEntityId: entityId,
              type:
                row?.config?.allowedTypes?.length > 1
                  ? (getEnumKeyByValue(Trace8ContactType, selectedContactType) as ContactType)
                  : (getEnumKeyByValue(Trace8ContactType, row?.config?.allowedTypes[0]) as ContactType),
              name,
              email,
              data: rest,
              tenantId
            }
          }
        });

        const { id } = createContactData.createContact;
        const contactIds = getContactIds(`${pageName}.${row.name}`);
        saveContactIds(`${pageName}.${row.name}`, row?.config?.selectType === 'multiple' ? [...(contactIds || []), id] : id);
        bankAccountsAndContactsRefetch();
        closeModalPage();
      }
    } catch (err) {
      const error = err as ApolloError;

      console.error(error);
      setErrorMessage(error?.message ?? defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent title="Saving Contact Info" isOpen={isSubmitting} onClose={() => null} showCloseIcon={false} showCancelButton={false}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {row?.config?.allowedTypes?.length > 1 && isModalWindowOpen && !isEditMode && (
        <ModalWrappedContent
          title="Add New Contact"
          isOpen={isModalWindowOpen}
          onClose={closeModalPage}
          confirmButton={{ name: 'Save', onClick: () => setIsModalWindowOpen(false), disabled: !selectedContactType }}
        >
          <ModalBody>
            <ModalHelperText>Indicate which type of entity to add as a {currentEditPage?.title?.toLowerCase() ?? ''}.</ModalHelperText>
            <RadioButtonsWrapper>
              {Object.values(Trace8ContactType).map((item) => (
                <RadioButton
                  key={item}
                  name="contactType"
                  radioButtonText={item}
                  selectedOption={selectedContactType}
                  handleRadioChange={(e) => {
                    setSelectedContactType(e.target.value as ContactType);
                    methods.setValue('contactType', e.target.value);
                  }}
                />
              ))}
            </RadioButtonsWrapper>
          </ModalBody>
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={closeModalPage} />} />
        <PageTitle title={row?.config?.title ?? 'Enter Contact Info'} />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <FormProvider {...methods}>
            <ContactInfoForm>
              <ContactInfoWrapper isMobile={isMobile}>
                <RowWrapper>
                  {rowsToRender?.map((row: IRenderTemplateRow, index: number) => (
                    <RowComponent
                      key={`${row.name}-${index}`}
                      row={row}
                      mode="documentationTab"
                      ignoreRequired={ignoreRequired}
                      openModalPage={() => {}}
                    />
                  ))}
                </RowWrapper>
              </ContactInfoWrapper>
              <FormFooter
                onCancel={closeModalPage}
                onSave={methods.handleSubmit(onSave, (error) => {
                  console.log(error);
                })}
                disableSaveButton={(methods.formState.isSubmitted && !methods.formState.isValid) || isSubmitting}
                showRequiredFieldsWarning={false}
              />
            </ContactInfoForm>
          </FormProvider>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const ContactInfoForm = styled.form`
  margin-top: 50px;
`;

const ContactInfoWrapper = styled.div<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '20px' : '40px')};
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${themeVar('layer.1')};
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '30px' : '40px')};
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
`;

const ModalHelperText = styled.p`
  padding: 10px 0;
  color: ${themeVar('font.base')};
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
`;
